// Typed access to the Django API (/api/*). All requests go through Django;
// the browser never talks to easydb directly.

import type { Group } from './tree'
import { toWire } from './tree'

export type Ref = { id: number | null; label: string; path?: string[]; path_ids?: number[] }
export type ImageVersion = { url: string; width: number | null; height: number | null }

export type Hit = {
  id: number
  uuid: string
  inv: string
  title: string
  title_html: string | null
  date: string
  year: number | null
  persons: Ref[]
  places: Ref[]
  keywords: Ref[]
  collections: Ref[]
  techniques: Ref[]
  photographer: Ref | null
  pool: Ref
  licensable: boolean
  can_license: boolean
  tags: number[]
  images: Partial<Record<'small' | 'preview' | 'huge' | 'full', ImageVersion>>
}

export type Detail = Hit & {
  description: string
  old_titles: string[]
  faces: (Ref & { unnamed?: boolean })[]
  gnd: { label: string; uri: string }[]
  copyright: string[]
  provenance: string[]
  license_note: string
  recorded: string | null
  file: { class: string; extension: string; width: number | null; height: number | null }
  internal?: Record<string, string | number | null>
}

export type FacetItem = { id: number; label: string; count: number; unnamed?: boolean }
export type TreeNode = FacetItem & { parent: number | null; path: string[]; path_ids: number[]; has_children?: boolean }
export type YearBin = { from: number; to: number; count: number; count_all: number }

export type Facets = {
  person: FacetItem[]
  gesicht: FacetItem[]
  ort: TreeNode[]
  schlagwort: TreeNode[]
  sammlung: TreeNode[]
  technik: FacetItem[]
  fotograf: FacetItem[]
  pool: TreeNode[]
  plattform: FacetItem[]
  jahr: YearBin[]
  /** true: the list holds only the biggest values, search on the server */
  more: Partial<Record<SearchFacet, boolean>>
  /** number of root nodes of the tree facets */
  roots: Record<TreeFacet, number>
}
export type TreeFacet = 'ort' | 'schlagwort' | 'sammlung'
export type SearchFacet = TreeFacet | 'person' | 'fotograf' | 'technik' | 'gesicht'

export type SearchResult = {
  total: number
  offset: number
  hits: Hit[]
  query: Group
  text: string
  warnings: string[]
  facets?: Facets
  took_ms: number
}

export type Suggestion = { field: string; id: number | null; label: string; path: string[]; count: number | null }

export type Me = {
  authenticated: boolean
  username: string | null
  email: string | null
  name: string | null
  is_researcher: boolean
  is_staff: boolean
  audience: 'public' | 'registered' | 'researcher'
  versions: string[]
  can_use_raw: boolean
  visible_total: number | null
}

export type Sort = 'relevance' | 'old' | 'new' | 'inv' | 'added'

export class ApiError extends Error {
  status: number
  kind?: string
  position?: number | null
  constructor(message: string, status: number, kind?: string, position?: number | null) {
    super(message)
    this.status = status
    this.kind = kind
    this.position = position
  }
}

function csrfToken() {
  const m = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : ''
}

export async function call<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  let body = init?.body
  if (init?.json !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(init.json)
  }
  if (init?.method && init.method !== 'GET') headers['X-CSRFToken'] = csrfToken()
  const res = await fetch(`/api${path}`, { ...init, body, headers, credentials: 'same-origin' })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const detail = typeof data.detail === 'string' ? data.detail : `Fehler ${res.status}`
    throw new ApiError(detail, res.status, data.kind, data.position)
  }
  return data as T
}

export const api = {
  search: (query: Group, opts: { sort: Sort; offset?: number; limit?: number; facets?: boolean }, signal?: AbortSignal) =>
    call<SearchResult>('/search', {
      method: 'POST', signal,
      json: { query: toWire(query), sort: opts.sort, offset: opts.offset ?? 0, limit: opts.limit ?? 48, facets: opts.facets ?? true },
    }),
  parse: (q: string) => call<{ query: Group; text: string }>('/parse', { method: 'POST', json: { q } }),
  format: (query: Group) => call<{ query: Group; text: string }>('/format', { method: 'POST', json: { query: toWire(query) } }),
  suggest: (q: string, signal?: AbortSignal) => call<Suggestion[]>(`/suggest?q=${encodeURIComponent(q)}`, { signal }),
  facetChildren: (query: Group, facet: TreeFacet, parent: number | null, limit: number, signal?: AbortSignal) =>
    call<{ items: TreeNode[]; total: number }>('/facets/children', { method: 'POST', signal, json: { query: toWire(query), facet, parent, limit } }),
  facetSearch: (query: Group, facet: SearchFacet, text: string, signal?: AbortSignal) =>
    call<TreeNode[]>('/facets/search', { method: 'POST', signal, json: { query: toWire(query), facet, text } }),
  object: (id: number) => call<Detail>(`/objects/${id}`),
  related: (id: number) => call<Hit[]>(`/objects/${id}/related`),
  me: () => call<Me>('/me'),
  csrf: () => call<{ csrftoken: string }>('/auth/csrf'),
  login: (username: string, password: string) => call<Me>('/auth/login', { method: 'POST', json: { username, password } }),
  logout: () => call<Me>('/auth/logout', { method: 'POST' }),
  register: (data: { email: string; password: string; first_name: string; last_name: string; organisation: string }) =>
    call<Me>('/auth/register', { method: 'POST', json: data }),
}
